import type { AreaPlateMode } from './api';

/**
 * Российский гос. номер: буква, 3 цифры, 2 буквы, 2–3 цифры региона.
 * Пример: A777AA77 → letter=A, digits=777, letters=AA, region=77, core=A777AA.
 */
export interface ParsedPlate {
  /** Нормализованный номер целиком (латиница, верхний регистр, без пробелов). */
  full: string;
  /** Номер без региона — «эта машина, любой регион». */
  core: string;
  /** Код региона (2–3 цифры). */
  region: string;
  /** Три цифры номера. */
  digits: string;
  /** Три буквы номера (первая + две после цифр). */
  letters: string;
}

const CYR_TO_LAT: Record<string, string> = {
  А: 'A', В: 'B', Е: 'E', К: 'K', М: 'M', Н: 'H',
  О: 'O', Р: 'P', С: 'C', Т: 'T', У: 'Y', Х: 'X',
};

/** Приводит ввод к виду, в котором номер сравнивает внешний API. */
export function normalizePlateInput(value: string): string {
  const upper = (value || '').toUpperCase().replace(/[\s-]/g, '');
  return upper.replace(/[А-Я]/g, (ch) => CYR_TO_LAT[ch] ?? ch);
}

const PLATE_RE = /^([A-Z])(\d{3})([A-Z]{2})(\d{2,3})$/;

/** null — ввод не похож на номер РФ. */
export function parsePlate(value: string): ParsedPlate | null {
  const normalized = normalizePlateInput(value);
  const m = PLATE_RE.exec(normalized);
  if (!m) return null;

  const [, letter1, digits, letters2, region] = m;
  return {
    full: normalized,
    core: `${letter1}${digits}${letters2}`,
    region,
    digits,
    letters: `${letter1}${letters2}`,
  };
}

export interface AreaPlateModeOption {
  value: AreaPlateMode;
  /** Подпись в выпадающем списке, подставляет части введённого номера. */
  label: string;
  /** Что именно попадёт в сравнение. */
  matchValue: string;
  /** Правило пускает много машин — помечаем в UI. */
  broad: boolean;
}

export const AREA_PLATE_MODES: AreaPlateMode[] = ['core', 'full', 'region', 'digits', 'letters'];

/**
 * Подписи режимов. Если номер ещё не распознан — показываем обобщённые подписи
 * без подстановки (региона/цифр/букв у нас пока нет).
 */
export function getAreaPlateModeOptions(parsed: ParsedPlate | null): AreaPlateModeOption[] {
  return [
    {
      value: 'core',
      label: 'Эта машина, любой регион',
      matchValue: parsed?.core ?? '',
      broad: false,
    },
    {
      value: 'full',
      label: 'Точно этот номер',
      matchValue: parsed?.full ?? '',
      broad: false,
    },
    {
      value: 'region',
      label: parsed ? `Все машины региона ${parsed.region}` : 'Все машины региона',
      matchValue: parsed?.region ?? '',
      broad: true,
    },
    {
      value: 'digits',
      label: parsed ? `Все номера с цифрами ${parsed.digits}` : 'Все номера с такими цифрами',
      matchValue: parsed?.digits ?? '',
      broad: true,
    },
    {
      value: 'letters',
      label: parsed ? `Все номера с буквами ${parsed.letters}` : 'Все номера с такими буквами',
      matchValue: parsed?.letters ?? '',
      broad: true,
    },
  ];
}

/** Подпись режима для таблицы уже добавленных номеров. */
export function getAreaPlateModeLabel(mode: string, plate: string): string {
  const parsed = parsePlate(plate);
  const option = getAreaPlateModeOptions(parsed).find((o) => o.value === mode);
  return option ? option.label.toLowerCase() : mode;
}

/** Правило пускает много машин (регион / цифры / буквы). */
export function isBroadMode(mode: string): boolean {
  return mode === 'region' || mode === 'digits' || mode === 'letters';
}

/**
 * Значение, по которому сравнивается номер. Если внешний API прислал своё — берём его.
 */
export function getAreaPlateMatchValue(mode: string, plate: string, valueFromApi?: string | null): string {
  if (typeof valueFromApi === 'string' && valueFromApi.trim() !== '') {
    return valueFromApi.trim();
  }
  const parsed = parsePlate(plate);
  const option = getAreaPlateModeOptions(parsed).find((o) => o.value === mode);
  return option?.matchValue || normalizePlateInput(plate);
}
